import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

/**
 * Fetches handwritten signature data from the API.
 * @param chainId - The ID of the delivery note chain.
 * @returns The signature data as a blob if successful.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_handwritten_signature_for_doc_asset_delivery_note_chain__doc_id__handwritten_signature_get
 */
export const fetchHandwrittenSignature = async (chainId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_HANDWRITTEN_SIGNATURE(chainId))
    .blob();

  return response;
};

/**
 * React Query based custom hook for getting a handwritten signature for a delivery note.
 * @param chainId - The ID of the delivery note chain.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryHandwrittenSignature = <TSelected = Blob>(
  chainId: UUID,
  options?: Omit<
    UseQueryOptions<Blob, unknown, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<Blob, unknown, TSelected>({
    enabled: Boolean(chainId),
    gcTime: ms('10m'),
    queryFn: async () => fetchHandwrittenSignature(chainId),
    queryKey: queryKeysDeliveryNote.getHandwrittenSignature(chainId),
    staleTime: Infinity,
    ...options,
  });
